import { extendTheme } from '@chakra-ui/react';
import { tableTheme } from './assets/themes/tableTheme';

// Seq4Life Design System Colors (restricted palette)
const colors = {
  primary: {
    50: '#ffffff',
    100: '#ffffff',
    200: '#d1d0e0',
    300: '#d1d0e0',
    400: '#1a1262',
    500: '#1a1262', // Main primary
    600: '#1a1262',
    700: '#1a1262',
    800: '#1a1262',
    900: '#1a1262',
  },
  surface: {
    50: '#ffffff',
    100: '#ffffff',
    200: '#d1d0e0',
    300: '#d1d0e0', // Main surface
    400: '#8c89b1',
    500: '#8c89b1',
  },
  secondary: {
    400: '#4f4987',
    500: '#4f4987', // Main secondary / highlight
  },
  muted: {
    300: '#8c89b1',
    400: '#8c89b1', // Main muted
    500: '#8c89b1',
  },
  white: '#ffffff',
  success: {
    500: '#22c55e',
    600: '#16a34a',
  },
  error: {
    500: '#ef4444',
    600: '#dc2626',
  },
};

// Typography
const fonts = {
  body: 'Montserrat, sans-serif',
  heading: 'Montserrat, sans-serif',
};

// Text Styles
const textStyles = {
  h1: {
    fontSize: { base: '2xl', md: '4xl' },
    fontWeight: 'bold',
    color: 'primary.500',
    lineHeight: 'shorter',
  },
  h2: {
    fontSize: { base: 'xl', md: '2xl' },
    fontWeight: 'bold',
    color: 'primary.500',
    lineHeight: 'short',
  },
  h3: {
    fontSize: { base: 'lg', md: 'xl' },
    fontWeight: 'semibold',
    color: 'primary.500',
    lineHeight: 'short',
  },
  body: {
    fontSize: 'md',
    fontWeight: 'normal',
    color: 'primary.500',
    lineHeight: 'tall',
  },
  bodyMuted: {
    fontSize: 'md',
    fontWeight: 'normal',
    color: 'muted.400',
    lineHeight: 'tall',
  },
  caption: {
    fontSize: 'sm',
    fontWeight: 'normal',
    color: 'muted.400',
    lineHeight: 'base',
  },
  label: {
    fontSize: 'xs',
    fontWeight: 'semibold',
    textTransform: 'uppercase',
    letterSpacing: 'wide',
    color: 'muted.400',
  },
};

// Layer Styles
const layerStyles = {
  card: {
    bg: 'white',
    borderRadius: '12px',
    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1)',
    border: '1px solid',
    borderColor: 'surface.300',
  },
  cardHover: {
    bg: 'white',
    borderRadius: '12px',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)',
    border: '1px solid',
    borderColor: 'surface.300',
    transition: 'all 0.2s ease',
    _hover: {
      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)',
      borderColor: 'primary.500',
    },
  },
  surface: {
    bg: 'surface.300',
    borderRadius: '12px',
  },
};

// Component Styles
const components = {
  Table: tableTheme,
  Button: {
    baseStyle: {
      fontWeight: 'semibold',
      borderRadius: '8px',
      transition: 'all 0.2s ease',
    },
    variants: {
      primary: {
        bg: 'primary.500',
        color: 'white',
        _hover: {
          bg: 'primary.600',
          transform: 'translateY(-1px)',
          boxShadow: 'md',
          _disabled: {
            bg: 'primary.500',
            transform: 'none',
          },
        },
        _active: {
          bg: 'primary.700',
          transform: 'translateY(0)',
        },
      },
      secondary: {
        bg: 'secondary.500',
        color: 'white',
        _hover: {
          bg: 'secondary.400',
          transform: 'translateY(-1px)',
          boxShadow: 'md',
        },
        _active: {
          bg: 'secondary.500',
          transform: 'translateY(0)',
        },
      },
      outline: {
        bg: 'transparent',
        color: 'primary.500',
        border: '2px solid',
        borderColor: 'primary.500',
        _hover: {
          bg: 'primary.50',
          transform: 'translateY(-1px)',
        },
        _active: {
          bg: 'primary.100',
          transform: 'translateY(0)',
        },
      },
      ghost: {
        bg: 'transparent',
        color: 'primary.500',
        _hover: {
          bg: 'surface.200',
        },
        _active: {
          bg: 'surface.300',
        },
      },
      link: {
        color: 'primary.500',
        _hover: {
          color: 'secondary.500',
          textDecoration: 'underline',
        },
      },
    },
    sizes: {
      sm: {
        fontSize: 'sm',
        px: 4,
        py: 2,
        h: '36px',
      },
      md: {
        fontSize: 'md',
        px: 6,
        py: 3,
        h: '44px',
      },
      lg: {
        fontSize: 'lg',
        px: 8,
        py: 4,
        h: '52px',
      },
    },
    defaultProps: {
      variant: 'primary',
      size: 'md',
    },
  },
  Input: {
    baseStyle: {
      field: {
        borderRadius: '8px',
      },
    },
    variants: {
      outline: {
        field: {
          bg: 'white',
          border: '1px solid',
          borderColor: 'surface.300',
          _hover: {
            borderColor: 'muted.400',
          },
          _focus: {
            borderColor: 'primary.500',
            boxShadow: '0 0 0 1px #1a1262',
          },
          _invalid: {
            borderColor: 'error.500',
            boxShadow: '0 0 0 1px #ef4444',
          },
        },
      },
      filled: {
        field: {
          bg: 'white',
          border: '1px solid',
          borderColor: 'surface.300',
          _hover: {
            borderColor: 'muted.400',
          },
          _focus: {
            bg: 'white',
            borderColor: 'primary.500',
            boxShadow: '0 0 0 1px #1a1262',
          },
        },
      },
    },
    sizes: {
      md: {
        field: {
          h: '44px',
          fontSize: 'md',
          px: 4,
        },
      },
      lg: {
        field: {
          h: '52px',
          fontSize: 'md',
          px: 4,
        },
      },
    },
    defaultProps: {
      variant: 'outline',
      size: 'md',
    },
  },
  FormLabel: {
    baseStyle: {
      fontSize: 'sm',
      fontWeight: 'medium',
      color: 'primary.500',
      mb: 2,
    },
  },
  FormError: {
    baseStyle: {
      text: {
        color: 'error.500',
        fontSize: 'sm',
        mt: 1,
      },
    },
  },
  Heading: {
    baseStyle: {
      color: 'primary.500',
      fontWeight: 'bold',
    },
  },
  Text: {
    baseStyle: {
      color: 'primary.500',
    },
    variants: {
      muted: {
        color: 'muted.400',
      },
    },
  },
  Link: {
    baseStyle: {
      color: 'primary.500',
      _hover: {
        color: 'primary.600',
        textDecoration: 'underline',
      },
    },
  },
  Modal: {
    baseStyle: {
      dialog: {
        bg: 'white',
        borderRadius: '16px',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
      },
      header: {
        color: 'primary.500',
        fontWeight: 'bold',
        fontSize: 'xl',
        pt: 6,
        pb: 2,
      },
      body: {
        py: 4,
      },
      footer: {
        pb: 6,
      },
      closeButton: {
        color: 'muted.400',
        _hover: {
          bg: 'surface.200',
        },
      },
    },
  },
  Card: {
    baseStyle: {
      container: {
        bg: 'white',
        borderRadius: '12px',
        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
        border: '1px solid',
        borderColor: 'surface.300',
      },
    },
  },
  Badge: {
    variants: {
      secondary: {
        bg: 'secondary.500',
        color: 'white',
        fontWeight: 'semibold',
      },
      accent: {
        bg: 'secondary.500',
        color: 'white',
        fontWeight: 'semibold',
      },
      primary: {
        bg: 'primary.500',
        color: 'white',
        fontWeight: 'semibold',
      },
      muted: {
        bg: 'surface.300',
        color: 'muted.400',
        fontWeight: 'semibold',
      },
    },
  },
  Divider: {
    baseStyle: {
      borderColor: 'surface.300',
    },
  },
};

// Global Styles
const styles = {
  global: {
    'html, body': {
      bg: 'white',
      color: 'primary.500',
      fontFamily: 'body',
    },
    '::selection': {
      bg: 'secondary.500',
      color: 'white',
    },
    '::-webkit-scrollbar': {
      width: '8px',
      height: '8px',
    },
    '::-webkit-scrollbar-track': {
      bg: 'surface.300',
    },
    '::-webkit-scrollbar-thumb': {
      bg: 'muted.400',
      borderRadius: '4px',
      _hover: {
        bg: 'muted.500',
      },
    },
  },
};

export const theme = extendTheme({
  colors,
  fonts,
  textStyles,
  layerStyles,
  components,
  styles,
});
